var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Total from './Total';
import Item from './Item';
import BookMeeting from './BookMeeting';
import T, { addTranslations } from '../../Utils/Locale';
import { fetchBasket } from '../Purchase/API';
import PurchaseType from '../Purchase/PurchaseType';
import { formatDate } from '../../Utils/DateFormatter';
import ErrorMessage from './ErrorMessage';
import translations from './translations.yml';
import Review from './Review';
import NextAction from './NextAction';
import Support from './Support';
import './style.css';
addTranslations(translations);
const BasketComponent = ({ preSelectedDate = null, preSelectedEmployee = null, isPreSelectedDateLocked = false }) => {
    const [isGoToLoading, toggleGoToLoading] = useState(false);
    const [basket, setBasket] = useState(null);
    const [bookingDate, setBookingDate] = useState(preSelectedDate);
    const [employee, setEmployee] = useState(preSelectedEmployee);
    const [isConditionsAccepted, toggleConditions] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);
    const [showDatePicker, toggleDatePicker] = useState(false);
    useEffect(() => {
        (() => __awaiter(void 0, void 0, void 0, function* () {
            const newBasket = yield fetchBasket();
            setBasket(newBasket);
        }))();
    }, []);
    const saveBooking = (selectedTimeSlot) => {
        if (selectedTimeSlot) {
            setBookingDate(selectedTimeSlot.start);
            setEmployee(selectedTimeSlot.resources[0].id);
        }
        else {
            setBookingDate(null);
            setEmployee(null);
        }
    };
    const removeBooking = () => {
        setBookingDate(null);
        setEmployee(null);
    };
    /**
     * Order items so items without an purchase type are prioritized.
     *
     * @returns {array}
     */
    const orderItems = () => basket.items.sort((a) => a.purchase_type ? 1 : -1);
    const updateBasket = () => __awaiter(void 0, void 0, void 0, function* () {
        const newBasket = yield fetchBasket();
        setBasket(newBasket);
    });
    const goToPayment = () => __awaiter(void 0, void 0, void 0, function* () {
        const messages = [];
        if (isItemMissingPurchaseTypeSelection()) {
            messages.push(T('basket.errors.purchase_type'));
        }
        if (basket.items.length === 0) {
            messages.push(T('basket.errors.empty'));
        }
        if (!isConditionsAccepted) {
            messages.push(T('basket.errors.conditions'));
        }
        if (isBookingRequired() && showDatePicker && !bookingDate) {
            messages.push(T('basket.errors.booking'));
        }
        setErrorMessages(messages);
        if (messages.length === 0) {
            toggleGoToLoading(true);
            const parameters = new URLSearchParams({});
            if (bookingDate && employee && !isPreSelectedDateLocked) {
                const selectedDate = new Date(bookingDate);
                parameters.append('date', formatDate(selectedDate));
                parameters.append('employee', employee);
            }
            parameters.append('autocapture', autoCapture());
            window.location.href = `/checkout?${parameters.toString()}`;
        }
    });
    const isItemMissingPurchaseTypeSelection = () => !!basket.items.find((item) => !item.purchase_type);
    const autoCapture = () => {
        return basket && basket.items.find((item) => item.purchase_type === PurchaseType.DIGITAL) === undefined;
    };
    const isBookingRequired = () => {
        const typesWhereBookingIsRequired = [PurchaseType.REVIEW, PurchaseType.ADVICE, PurchaseType.UPGRADE_TO_REVIEW, PurchaseType.UPGRADE_TO_ADVICE];
        return basket && !!basket.items.find((item) => typesWhereBookingIsRequired.indexOf(item.purchase_type) !== -1);
    };
    return (_jsx("div", Object.assign({ className: 'w-full h-full' }, { children: _jsxs("div", Object.assign({ className: 'container mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 p-5 md:py-0 lg:px-2' }, { children: [_jsxs("div", Object.assign({ className: 'product-list relative col-span-1 md:col-span-8' }, { children: [!basket && (_jsx("div", Object.assign({ className: 'card-spinner w-full h-full absolute left-0 top-0 bg-transparent z-20' }, { children: _jsx("div", { className: 'card-spinner--inner' }) }))), errorMessages.length > 0 && (_jsx(ErrorMessage, { messages: errorMessages, setMessages: setErrorMessages })), basket && basket.items.length === 0 && (_jsx("p", Object.assign({ className: 'text-gray-69 font-light' }, { children: T('basket.empty') }))), isBookingRequired() && (_jsx(BookMeeting, { basket: basket, saveBooking: saveBooking, removeBooking: removeBooking, bookingDate: bookingDate, isPreBooked: isPreSelectedDateLocked, showDatePicker: showDatePicker, toggleDatePicker: toggleDatePicker })), basket && orderItems().map((item) => (_jsx(Item, { item: item, updateBasket: updateBasket }, item.id))), basket && basket.items.length > 0 && (_jsx(NextAction, { items: basket.items.filter((item) => item.purchase_type), hasSelectedBookingTime: bookingDate !== null }))] })), _jsxs("div", Object.assign({ className: 'content-aside col-span-1 md:col-span-4' }, { children: [_jsx(Review, {}), _jsx(Total, { basket: basket, goToPayment: goToPayment, isLoading: isGoToLoading, toggleConditions: toggleConditions }), _jsx(Support, {})] }))] })) })));
};
BasketComponent.propTypes = {
    preSelectedDate: PropTypes.string,
    preSelectedEmployee: PropTypes.string,
    isPreSelectedDateLocked: PropTypes.bool
};
export default BasketComponent;
